import {
  Alert,
  Box,
  Button,
  Divider,
  Group,
  Loader,
  Paper,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
} from "@mantine/core";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertCircle, School } from "tabler-icons-react";
import { useGetClassesQuery } from "../../../redux/features/classeApi";
import { useLazyGetActivitiesGlobalStatsQuery } from "../../dasboard/api/features/queriesApi";

// Config of columns / feature styles
const FEATURE_STYLES = {
  DIAGNOSTIC_LECTURE: { label: "Diag. Lecture", color: "#10b981" }, // Teal/Green
  DIAGNOSTIC_MATHS: { label: "Diag. Maths", color: "#f97316" }, // Orange
  LESSON_QUIZ: { label: "Vérifier la leçon", color: "#1e40af" }, // Dark Blue
  EXERCISE_CORRECTION: { label: "Corriger l'exercice", color: "#3b82f6" }, // Light Blue
};

// Colors fallback generator if feature not in style list
const getFeatureStyle = (type, name) => {
  if (FEATURE_STYLES[type]) {
    return {
      ...FEATURE_STYLES[type],
      label: name || FEATURE_STYLES[type].label,
    };
  }
  // Simple hash for consistent coloring
  const colors = [
    "#228be6",
    "#f97316",
    "#7c3aed",
    "#10b981",
    "#ec4899",
    "#06b6d4",
  ];
  const index =
    type.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0) %
    colors.length;
  return { label: name || type, color: colors[index] };
};

const BASE_GRADES = [
  "Primaire",
  "CI",
  "CP",
  "CE1",
  "CE2",
  "CM1",
  "CM2",
  "Collège",
  "Lycée",
  "Préscolaire",
];

const LOCAL_FUNNEL_CONFIG = [
  { key: "USER", label: "User" },
  { key: "USER_ACTIVE", label: "User Activé" },
  { key: "CLIENT", label: "Client" },
  { key: "CLIENT_ENGAGE", label: "Client engagé" },
  { key: "CLIENT_ACTIF", label: "Client actif" },
];

const PERIOD_LABELS = {
  today: "Aujourd'hui",
  yesterday: "Hier",
  "last-week": "Semaine dernière",
  "last-month": "Mois dernier",
  week: "Cette semaine",
  month: "Ce mois",
  year: "Cette année",
  all: "Tout",
  custom: "Personnalisé",
};

// Row component for each school grade
function GradeFeatureRow({
  grade,
  classIds,
  selectedCategories,
  startDate,
  endDate,
  onLoaded,
}) {
  const [trigger] = useLazyGetActivitiesGlobalStatsQuery();
  const [aggregated, setAggregated] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchAll = async () => {
      setIsLoading(true);
      try {
        const categoriesToQuery =
          selectedCategories.length === 0 ? [undefined] : selectedCategories;
        const classesToQuery = classIds.length === 0 ? [undefined] : classIds;

        const promises = [];
        classesToQuery.forEach((clsId) => {
          categoriesToQuery.forEach((catKey) => {
            promises.push(
              trigger({
                start_date: startDate,
                end_date: endDate,
                classe_id: clsId,
                category: catKey,
              }).unwrap(),
            );
          });
        });

        const results = await Promise.all(promises);

        // Aggregate
        let total_activities = 0;
        const activities_by_type = {};

        results.forEach((res) => {
          if (!res) return;
          total_activities += res.total_activities || 0;
          const byType = res.activities_by_type || {};
          Object.keys(byType).forEach((type) => {
            if (!activities_by_type[type]) {
              activities_by_type[type] = {
                name: byType[type].name,
                count: 0,
                percentage: 0,
              };
            }
            activities_by_type[type].count += byType[type].count || 0;
          });
        });

        // Recompute percentages
        Object.keys(activities_by_type).forEach((type) => {
          const item = activities_by_type[type];
          item.percentage =
            total_activities > 0
              ? parseFloat(((item.count / total_activities) * 100).toFixed(2))
              : 0;
        });

        const finalData = {
          total_activities,
          activities_by_type,
        };

        setAggregated(finalData);
        if (onLoaded) {
          onLoaded(grade, finalData);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAll();
  }, [
    classIds,
    selectedCategories,
    startDate,
    endDate,
    trigger,
    grade,
    onLoaded,
  ]);

  if (isLoading) {
    return (
      <Group spacing="md" py="xs" align="center" style={{ width: "100%" }}>
        <Text weight={700} size="sm" style={{ width: "80px" }}>
          {grade}
        </Text>
        <Box
          style={{
            flex: 1,
            height: "24px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Loader size="xs" />
        </Box>
        <Text
          size="xs"
          color="dimmed"
          style={{ width: "80px", textAlign: "right" }}
        >
          Chargement...
        </Text>
      </Group>
    );
  }

  const total = aggregated?.total_activities || 0;
  const byType = aggregated?.activities_by_type || {};
  const activeFeatures = Object.keys(byType).filter(
    (key) => byType[key].count > 0,
  );

  return (
    <Group spacing="md" py={8} align="center" style={{ width: "100%" }}>
      {/* Grade Label */}
      <Text weight={700} size="sm" style={{ width: "80px", color: "#495057" }}>
        {grade}
      </Text>

      {/* Stacked Horizontal Bar */}
      <Box
        style={{
          flex: 1,
          height: "28px",
          backgroundColor: "#f1f3f5",
          borderRadius: "14px",
          overflow: "hidden",
          display: "flex",
          boxShadow: "inset 0 1px 3px rgba(0,0,0,0.05)",
        }}
      >
        {total > 0 ? (
          activeFeatures.map((key) => {
            const item = byType[key];
            const style = getFeatureStyle(key, item.name);
            const percentage = item.percentage;
            const count = item.count;

            return (
              <Tooltip
                key={key}
                label={
                  <Box p={4}>
                    <Text weight={700} size="xs">
                      {style.label}
                    </Text>
                    <Text size="xs" mt={2}>
                      {count} activités ({Math.round(percentage)}%)
                    </Text>
                  </Box>
                }
                withArrow
                withinPortal
              >
                <Box
                  style={{
                    width: `${percentage}%`,
                    backgroundColor: style.color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "width 0.3s ease",
                    cursor: "pointer",
                    "&:hover": {
                      filter: "brightness(0.95)",
                    },
                  }}
                >
                  {percentage >= 6 && (
                    <Text weight={800} size="xs" color="white">
                      {Math.round(percentage)}%
                    </Text>
                  )}
                </Box>
              </Tooltip>
            );
          })
        ) : (
          <Box
            style={{
              width: "100%",
              height: "100%",
              backgroundColor: "#f8f9fa",
              border: "1px dashed #dee2e6",
              borderRadius: "14px",
            }}
          />
        )}
      </Box>

      {/* Total Display */}
      <Text
        size="xs"
        weight={600}
        color={total > 0 ? "dimmed" : "red"}
        style={{ width: "80px", textAlign: "right" }}
      >
        {total} act.
      </Text>
    </Group>
  );
}

export default function GradeFeatureStats({
  dateRange,
  periodFilter,
  selectedGrades,
}) {
  const [localCategories, setLocalCategories] = useState(new Set());
  const [gradesDataMap, setGradesDataMap] = useState({});

  const startDate = dateRange.start.format("YYYY-MM-DD");
  const endDate = dateRange.end.format("YYYY-MM-DD");

  // Fetch classes from database
  const { data: classesData, error: errorClasses } = useGetClassesQuery();

  const handleToggleCategory = (catKey) => {
    setLocalCategories((prev) => {
      const next = new Set(prev);
      if (next.has(catKey)) {
        next.delete(catKey);
      } else {
        next.add(catKey);
      }
      return next;
    });
  };

  const handleClearCategories = () => {
    setLocalCategories(new Set());
  };

  const handleRowLoaded = useCallback((grade, data) => {
    setGradesDataMap((prev) => {
      if (JSON.stringify(prev[grade]) === JSON.stringify(data)) return prev;
      return { ...prev, [grade]: data };
    });
  }, []);

  // Compute matched class IDs for each grade
  const gradeClassesMap = useMemo(() => {
    const map = {};
    BASE_GRADES.forEach((grade) => {
      map[grade] = (classesData?.data || [])
        .filter((cls) => {
          if (!cls.name) return false;
          const upper = cls.name.toUpperCase();
          if (grade === "Primaire") {
            return ["CI", "CP", "CE1", "CE2", "CM1", "CM2", "PRIMAIRE"].some(
              (g) => upper.includes(g),
            );
          }
          return upper.includes(grade.toUpperCase());
        })
        .map((cls) => cls.id);
    });
    return map;
  }, [classesData]);

  // Context Summaries
  const periodSummary = PERIOD_LABELS[periodFilter] || "Ce mois";

  const classesSummary = useMemo(() => {
    if (selectedGrades.size === 0) return "Toutes";
    return Array.from(selectedGrades).join(", ");
  }, [selectedGrades]);

  const funnelSummary = useMemo(() => {
    if (localCategories.size === 0) return "Toutes";
    return Array.from(localCategories)
      .map(
        (key) => LOCAL_FUNNEL_CONFIG.find((c) => c.key === key)?.label || key,
      )
      .join(", ");
  }, [localCategories]);

  // Extract all unique feature keys across loaded data to build dynamic legend
  const detectedFeatures = useMemo(() => {
    const keys = new Set();
    Object.values(gradesDataMap).forEach((data) => {
      if (!data?.activities_by_type) return;
      Object.keys(data.activities_by_type).forEach((k) => {
        const item = data.activities_by_type[k];
        if (item?.count > 0) {
          keys.add(k);
        }
      });
    });
    return Array.from(keys);
  }, [gradesDataMap]);

  return (
    <Box mt="xl">
      {/* Title block */}
      <Box mb="md">
        <Group spacing="sm" align="center">
          <ThemeIcon variant="light" color="blue" size={32} radius="md">
            <School size={20} />
          </ThemeIcon>
          <Title
            order={3}
            sx={{ fontSize: "20px", fontWeight: 700, color: "#1c7ed6" }}
          >
            Vue 2 — Feature × Classe
          </Title>
        </Group>
        <Text size="sm" color="dimmed" mt={4}>
          Quelle fonctionnalité préfèrent les parents selon la classe de leur
          enfant ?
        </Text>
      </Box>

      {/* Filter summary context reminder */}
      <Paper
        p="xs"
        radius="md"
        withBorder
        mb="lg"
        sx={{ backgroundColor: "#f8f9fa", borderLeft: "4px solid #228be6" }}
      >
        <Text
          size="xs"
          color="dimmed"
          weight={600}
          style={{ textTransform: "none" }}
        >
          Context : Période : <strong>{periodSummary}</strong> · Classe :{" "}
          <strong>{classesSummary}</strong> · Étape Funnel :{" "}
          <strong>{funnelSummary}</strong>
        </Text>
      </Paper>

      {/* Local Filter for Funnel Stages */}
      <Paper
        p="md"
        radius="lg"
        shadow="sm"
        withBorder
        mb="lg"
        sx={{ backgroundColor: "#ffffff" }}
      >
        <Text
          size="xs"
          weight={700}
          color="dimmed"
          transform="uppercase"
          mb="xs"
          letterSpacing={0.5}
        >
          Étape Funnel — filtre local Vue 2 · multi-sélection · combinable avec
          filtre Classe global
        </Text>
        <Group spacing="xs">
          <Button
            size="xs"
            radius="xl"
            variant={localCategories.size === 0 ? "filled" : "light"}
            color={localCategories.size === 0 ? "dark" : "gray"}
            onClick={handleClearCategories}
            styles={{
              root: {
                fontWeight: 600,
                backgroundColor:
                  localCategories.size === 0 ? "#1a202c" : "#f1f3f5",
                color: localCategories.size === 0 ? "#ffffff" : "#495057",
                "&:hover": {
                  backgroundColor:
                    localCategories.size === 0 ? "#2d3748" : "#e9ecef",
                },
              },
            }}
          >
            Toutes
          </Button>

          {LOCAL_FUNNEL_CONFIG.map((cat) => {
            const isActive = localCategories.has(cat.key);
            return (
              <Button
                key={cat.key}
                size="xs"
                radius="xl"
                variant={isActive ? "filled" : "outline"}
                color={isActive ? "blue" : "gray"}
                onClick={() => handleToggleCategory(cat.key)}
                leftIcon={
                  <div
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      backgroundColor: isActive ? "#ffffff" : "#adb5bd",
                    }}
                  />
                }
                styles={{
                  root: {
                    fontWeight: 600,
                    borderColor: isActive ? "#3b82f6" : "#dee2e6",
                    backgroundColor: isActive ? "#3b82f6" : "#ffffff",
                    color: isActive ? "#ffffff" : "#495057",
                    "&:hover": {
                      backgroundColor: isActive ? "#2563eb" : "#f8f9fa",
                    },
                  },
                }}
              >
                {cat.label}
              </Button>
            );
          })}
        </Group>
      </Paper>

      {/* Main stacked bar lists */}
      {errorClasses ? (
        <Alert icon={<AlertCircle size={16} />} title="Erreur" color="red">
          Impossible de charger la liste des classes.
        </Alert>
      ) : (
        <Paper
          p="xl"
          radius="lg"
          shadow="sm"
          withBorder
          sx={{ backgroundColor: "#ffffff" }}
        >
          {BASE_GRADES.map((grade) => (
            <GradeFeatureRow
              key={grade}
              grade={grade}
              classIds={gradeClassesMap[grade] || []}
              selectedCategories={Array.from(localCategories)}
              startDate={startDate}
              endDate={endDate}
              onLoaded={handleRowLoaded}
            />
          ))}

          {/* Dynamic Legend at the bottom */}
          <Divider my="md" />
          <Group spacing="xl" position="center">
            {(detectedFeatures.length > 0
              ? detectedFeatures
              : [
                  "DIAGNOSTIC_LECTURE",
                  "DIAGNOSTIC_MATHS",
                  "LESSON_QUIZ",
                  "EXERCISE_CORRECTION",
                ]
            ).map((featKey) => {
              const style = getFeatureStyle(featKey);
              return (
                <Group key={featKey} spacing={6}>
                  <div
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: "2px",
                      backgroundColor: style.color,
                    }}
                  />
                  <Text size="xs" color="dimmed" weight={600}>
                    {style.label}
                  </Text>
                </Group>
              );
            })}
          </Group>
        </Paper>
      )}
    </Box>
  );
}
